import * as React from "react";

import { MODALS } from "@typings/modals";
import {
  AVAILABLE_COUNTRIES,
  ORIGIN,
  WHATS_INCLUDED_TYPE,
} from "@typings/globals";
import { AppConfig } from "@typings/appConfig";

import { Margins } from "@ui/Atoms/Margins";
import { Heading } from "@ui/Atoms/Headings";
import {
  RowProps,
  WhatIsIncludedTable,
} from "@ui/Molecules/WhatIsIncludedTable";
import { ModalContentWhatIsIncluded } from "@ui/Organisms/ModalContentWhatIsIncluded";

import { useConfig } from "@hooks/useConfig";
import { useTranslate } from "@hooks/useTranslate";
import { useLocalisation } from "@hooks/useLocalisation";

import { Modal } from "@app/containers/Modal";
import { useModalStore } from "@app/hooks/useModalStore";

const WhatIsIncludedModal = (): React.ReactElement => {
  const whatIsIncluded: AppConfig["booking"]["whatIsIncluded"] = useConfig([
    "booking",
    "whatIsIncluded",
  ]);

  const { hideModal } = useModalStore();
  const translate = useTranslate("batmaid_pwa_booking");

  const { language, country } = useLocalisation();

  const getTableHeaders: () => string[] = React.useCallback(
    () =>
      whatIsIncluded?.headers.map(header =>
        translate(`batmaid_pwa_generic.${header}`),
      ) ?? [],
    [whatIsIncluded, translate],
  );
  const getTableRows: () => RowProps[] = React.useCallback(
    () =>
      [
        {
          label: translate("same_agent_label"),
          values: [],
        },
        {
          label:
            country === AVAILABLE_COUNTRIES.NL
              ? translate("free_cancelation_up_to_label_nl")
              : translate("free_cancelation_up_to_label"),
          values: [],
        },
        {
          label: translate("damage_insurance_label"),
          values: [],
        },
        {
          label: translate("dedicated_support_label"),
          values: [],
        },
        {
          label: translate("free_secure_payment_label"),
          values: [],
        },
      ].map((row, index) => ({
        ...row,
        values: whatIsIncluded?.rows[index] ?? [],
      })),
    [whatIsIncluded, translate],
  );

  return (
    <Modal
      title={translate("modal_what_is_included_title")}
      id={MODALS.BOOKING_WHAT_IS_INCLUDED}
      size="large"
      submit={() => hideModal(MODALS.BOOKING_WHAT_IS_INCLUDED)}
      withCancelBtn={false}
      submitBtnColor={"blue"}
      submitText={translate("modal_what_is_included_submit")}
      withContainedHeight
    >
      <Margins xs={[null, "base_x6", null, null]}>
        {!!whatIsIncluded && (
          <WhatIsIncludedTable
            headers={getTableHeaders()}
            rows={getTableRows()}
          />
        )}
      </Margins>

      <Margins xs={[null, "base_x2", null, null]}>
        <Heading
          styleAs="h6"
          type="h2"
          textAlignXs="center"
          textAlignMd="center"
          textAlignLg="center"
        >
          {translate("whats_included_in_your_cleaning_heading")}
        </Heading>
      </Margins>

      <ModalContentWhatIsIncluded
        origin={ORIGIN.BATMAID}
        lang={language}
        country={country}
        contentType={WHATS_INCLUDED_TYPE.BOOKING}
      />
    </Modal>
  );
};

export { WhatIsIncludedModal };

WhatIsIncludedModal.displayName = "WhatIsIncludedModal";
